// The album rows inside a picker sheet, shared by the two places that need one.
//
// W39.54. `components/calendar/AlbumPickerSheet.tsx` (Q21, link a calendar event
// to an album) and the wish photo block's picker (attach photos from an album)
// want the same list: cover thumbnail, name, item count, one tap to choose. The
// second one appeared because `POST /wishes/{id}/blocks/{id}/photos` had existed
// since W3 with no UI able to reach it — a whole API with no door.
//
// Sharing the *rows* rather than the sheet is the honest amount of reuse: the
// two sheets have different titles, different copy, and one of them has a
// second step, so sharing the sheet would mean threading a "then what" prop
// through it. The query is shared too — it goes through `useAlbumList`, so both
// sheets read the same cache key and cannot disagree about what exists.

import { Image as ImageIcon } from "lucide-react";

import { LoadingState } from "@/components/primitives/LoadingState";
import { useAlbumList } from "@/lib/hooks/album";
import { mediaFileUrl } from "@/lib/api/album";
import { cn } from "@/lib/utils/cn";

export interface AlbumListRowsProps {
  /** Skip the request while the sheet is closed. */
  enabled: boolean;
  onPick: (albumId: string) => void;
  busy?: boolean;
  /** Hide albums with no photos — the photo picker has nothing to offer. */
  onlyWithPhotos?: boolean;
  emptyLabel?: string;
  testId?: string;
}

export function AlbumListRows({
  enabled,
  onPick,
  busy = false,
  onlyWithPhotos = false,
  emptyLabel = "還沒有相簿",
  testId = "album-picker-rows",
}: AlbumListRowsProps) {
  const { data, isLoading } = useAlbumList({ enabled });
  if (!enabled) return null;

  const albums = (data ?? []).filter((a) => (onlyWithPhotos ? a.item_count > 0 : true));

  if (isLoading) return <LoadingState shape="row" count={3} />;
  if (albums.length === 0) {
    return (
      <p className="text-body-sm text-ink-2" data-testid={`${testId}-empty`}>
        {onlyWithPhotos ? "還沒有含照片的相簿" : emptyLabel}
      </p>
    );
  }

  return (
    <ul className="flex flex-col divide-y divide-hairline" data-testid={testId}>
      {albums.map((album) => (
        <li key={album.id}>
          <button
            type="button"
            disabled={busy}
            onClick={() => onPick(album.id)}
            className={cn(
              "flex w-full items-center gap-s-3 py-s-3 text-left",
              busy ? "opacity-50" : "hover:bg-black/5",
            )}
            data-testid={`album-option-${album.id}`}
          >
            {album.cover_media ? (
              <img
                src={mediaFileUrl(album.cover_media.id)}
                alt=""
                className="h-8 w-8 shrink-0 rounded-xs object-cover"
              />
            ) : (
              <ImageIcon size={18} className="shrink-0 text-ink-3" aria-hidden />
            )}
            <span className="flex-1 truncate text-body text-ink-1">{album.name}</span>
            {typeof album.item_count === "number" ? (
              <span className="shrink-0 text-meta text-ink-3">{album.item_count} 張</span>
            ) : null}
          </button>
        </li>
      ))}
    </ul>
  );
}
